import { DashboardState, LeftPanelType } from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";

/** A screen in the current app. */
export interface IScreenUsageTarget {
  kind: "screen";
  id: string;
}

/** A process (workflow) anywhere in the domain. */
export interface IWorkflowUsageTarget {
  kind: "workflow";
  id: string;
}

export interface IScheduledJobUsageTarget {
  kind: "scheduledJob";
  id: string;
}

export interface IApiEndpointUsageTarget {
  kind: "apiEndpoint";
  id: string;
}

/**
 * Where an "Open" link in the Where Used report points (KOA-17818). A discriminated union rather
 * than an id plus a string tag, so a new usage source cannot be added without the navigation
 * switch failing to compile.
 */
export type ActionUsageTarget =
  | IScreenUsageTarget
  | IWorkflowUsageTarget
  | IScheduledJobUsageTarget
  | IApiEndpointUsageTarget;

/**
 * The open screen is saved before leaving it — the same guard the screens list and the process
 * redirect use. A failed save must not trap the user in the report, so it is swallowed: the worst
 * case is the autosave picking it up, while throwing here would make "Open" silently do nothing.
 */
async function saveOpenScreen(sd: IScreenDesignContextState): Promise<void> {
  try {
    await sd.saveScreen(true);
  } catch {
    // Deliberately ignored — see above.
  }
}

/**
 * Navigates the designer to the place a Where Used row names (KOA-17818).
 *
 * Each branch mirrors the tool's own entry point rather than inventing a route: screens go through
 * `loadScreen` like the screens list, processes through the dashboard + `setSelectedWf` pair that
 * `redirect-to-process-forms` established, and jobs and endpoints through their providers' own
 * active-entity setters. Keeping to those paths is what makes presence reporting, panel state and
 * collab sessions behave as if the user had navigated by hand.
 */
export async function navigateToActionUsage(sd: IScreenDesignContextState, target: ActionUsageTarget): Promise<void> {
  await saveOpenScreen(sd);

  switch (target.kind) {
    case "screen": {
      if (sd.dashboardState !== DashboardState.design) {
        sd.setDashboardState(DashboardState.design);
        sd.setActiveLeftPanelByClick(LeftPanelType.Screens);
      }
      await sd.loadScreen(target.id);
      return;
    }
    case "workflow": {
      sd.setDashboardState(DashboardState.workFlow);
      sd.setActiveLeftPanelByClick(LeftPanelType.WorkFlowList);
      await sd.setSelectedWf(target.id);
      return;
    }
    case "scheduledJob": {
      sd.setDashboardState(DashboardState.scheduledJobs);
      sd.setActiveLeftPanelByClick(LeftPanelType.ScheduledJobs);
      sd.setActiveScheduledJob(target.id);
      return;
    }
    case "apiEndpoint": {
      sd.setDashboardState(DashboardState.apiManagement);
      sd.setActiveLeftPanelByClick(LeftPanelType.ApiManagement);
      sd.setActiveApi(target.id);
      return;
    }
  }
}
